import { ImageUpload } from "./ImageUpload";
import defaultLogoSvg from "../../../imports/MoechaLogo.svg";
import { useState, useEffect } from "react";
import { useAdminData, type SiteConfig, type PromoCode, type Review } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { isSupabaseConnected, fetchSiteConfig, upsertSiteConfig, flatToSiteConfig, siteConfigToFlat } from "../supabase";
import { Save, Check, Plus, Trash2, Star, CheckCircle, XCircle } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { TranslationsPanel } from "./TranslationsPanel";
import { AdminThemeTab } from "./AdminThemeTab";
import { AdminFAQTab } from "./AdminFAQTab";
import { AdminWholesaleTab } from "./AdminWholesaleTab";

export function AdminSiteConfig() {
  const {
    siteConfig, setSiteConfig,
    promoCodes, addPromoCode, updatePromoCode, deletePromoCode, getNextPromoCodeId,
    reviews, updateReview, deleteReview, products,
  } = useAdminData();
  const { hasPermission } = useAdminAuth();
  const [config, setConfig] = useState<SiteConfig>(JSON.parse(JSON.stringify(siteConfig)));
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"brand" | "hero" | "about" | "features" | "theme" | "faq" | "wholesale" | "promos" | "reviews">("brand");

  // Sync local config from context when siteConfig changes (e.g. after load)
  useEffect(() => {
    setConfig(JSON.parse(JSON.stringify(siteConfig)));
  }, [siteConfig]);

  // Load site config from Supabase on mount when connected
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    fetchSiteConfig()
      .then(({ data, error }) => {
        if (error || !data) return;
        const flat = flatToSiteConfig(data as Record<string, unknown>);
        const merged: SiteConfig = {
          ...siteConfig,
          ...flat,
          logo: (flat.logo as string) ?? siteConfig.logo,
          contact_email: (flat.contact_email as string) ?? siteConfig.contact_email,
          contact_phone: (flat.contact_phone as string) ?? siteConfig.contact_phone,
          contact_address: (flat.contact_address as string) ?? siteConfig.contact_address,
          hero: { ...siteConfig.hero, ...(flat.hero as object) },
          about: { ...siteConfig.about, ...(flat.about as object) },
          features: { ...siteConfig.features, ...(flat.features as object) },
        };
        setConfig(merged);
        setSiteConfig(merged);
      })
      .catch(() => {});
  }, []);

  if (!hasPermission("site_config")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don't have access to this section.</p>;
  }

  const handleSave = async () => {
    setSaveError(null);
    setSaveLoading(true);
    if (isSupabaseConnected()) {
      const { error } = await upsertSiteConfig(siteConfigToFlat(config as Record<string, unknown>));
      if (error) {
        setSaveError(error.message);
        setSaveLoading(false);
        return;
      }
    }
    setSiteConfig(config);
    setSaved(true);
    setSaveLoading(false);
    setTimeout(() => setSaved(false), 2000);
  };

  const features = config.features || { reviews: false, newsletter: false, promoCodes: false, faq: false, wishlist: true, account: false };

  const toggleFeature = (key: keyof typeof features) => {
    setConfig({
      ...config,
      features: { ...features, [key]: !features[key] },
    });
  };

  const tabs = [
    { id: "brand" as const, label: "Brand" },
    { id: "hero" as const, label: "Hero" },
    { id: "about" as const, label: "About" },
    { id: "wholesale" as const, label: "Wholesale" },
    { id: "features" as const, label: "Features" },
    { id: "theme" as const, label: "Theme" },
    { id: "faq" as const, label: "FAQ" },
    { id: "promos" as const, label: "Promo Codes" },
    { id: "reviews" as const, label: "Reviews" },
  ];

  const pendingReviews = reviews.filter((r) => !r.approved);
  const approvedReviews = reviews.filter((r) => r.approved);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1
          className="text-[#2D2A26] tracking-[0.08em]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
        >
          Site Configuration
        </h1>
        <div className="flex flex-col items-end gap-1">
          {saveError && (
            <p className="text-[#8B5C58]" style={{ fontSize: "12px" }}>{saveError}</p>
          )}
          <button
            onClick={handleSave}
            disabled={saveLoading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all duration-300 tracking-[0.1em] uppercase disabled:opacity-60 ${
              saved ? "bg-[#74856D] text-white" : "bg-[#6F7F63] text-white hover:bg-[#5F6F53]"
            }`}
            style={{ fontSize: "11px" }}
          >
            {saved ? <Check size={16} strokeWidth={1.5} /> : <Save size={16} strokeWidth={1.5} />}
            {saved ? "Saved" : saveLoading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg tracking-[0.1em] uppercase transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-[#6F7F63] text-white"
                : "bg-white text-[#6F675F] hover:bg-[#F2EEE8]"
            }`}
            style={{ fontSize: "11px" }}
          >
            {tab.label}
            {tab.id === "reviews" && pendingReviews.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-[#C7A76C] text-white rounded-full" style={{ fontSize: "9px" }}>
                {pendingReviews.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Brand Tab */}
      {activeTab === "brand" && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Brand Logo
          </h2>
          <p className="text-[#6F675F] mb-6" style={{ fontSize: "13px", fontWeight: 300 }}>
            Upload your brand logo. It will appear in the navigation bar and footer. For best results, use a transparent SVG or PNG.
            When no custom logo is uploaded the default MOECHA mark is used.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ImageUpload
                label="Custom Logo"
                value={config.logo || ""}
                onChange={(v) => setConfig({ ...config, logo: v })}
                accept={["image/svg+xml", "image/png", "image/jpeg"]}
                acceptLabel="SVG, PNG, JPG"
                maxSizeBytes={512 * 1024}
                maxSizeLabel="500 KB"
                previewAspect="aspect-[3/1]"
              />

              {/* Supabase bucket note */}
              <div className="mt-4 flex items-start gap-3 bg-[#6F7F63]/5 border border-[#6F7F63]/15 rounded-lg px-4 py-3">
                <div className="shrink-0 w-5 h-5 rounded-full bg-[#6F7F63]/10 flex items-center justify-center mt-0.5">
                  <span className="text-[#6F7F63]" style={{ fontSize: "12px" }}>i</span>
                </div>
                <div>
                  <p className="text-[#2D2A26] mb-0.5" style={{ fontSize: "12px" }}>
                    Supabase Storage
                  </p>
                  <p className="text-[#6F675F]" style={{ fontSize: "11px", fontWeight: 300 }}>
                    When a Supabase bucket is connected, uploaded logos are persisted in cloud storage and will override this default.
                    Without Supabase the logo is stored in your browser only.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              {(() => {
                const previewSrc = config.logo || defaultLogoSvg;
                return (
                  <div className="flex flex-col items-center gap-6">
                    {/* Current state label */}
                    <p className="text-[#6F675F] tracking-[0.08em] uppercase" style={{ fontSize: "10px" }}>
                      {config.logo ? "Custom logo" : "Default logo (seed)"}
                    </p>
                    {/* Nav preview */}
                    <div>
                      <p className="text-[#D8D1C7] tracking-[0.12em] uppercase text-center mb-3" style={{ fontSize: "9px" }}>Navigation preview</p>
                      <div className="bg-[#F7F5F1] rounded-xl px-8 py-4 flex items-center justify-center border border-[#E6E0D7]">
                        <img src={previewSrc} alt="Logo preview" className="h-10 object-contain" />
                      </div>
                    </div>
                    {/* Footer preview */}
                    <div>
                      <p className="text-[#D8D1C7] tracking-[0.12em] uppercase text-center mb-3" style={{ fontSize: "9px" }}>Footer preview</p>
                      <div className="bg-[#2D2A26] rounded-xl px-8 py-4 flex items-center justify-center">
                        <img src={previewSrc} alt="Logo preview" className="h-10 object-contain brightness-0 invert" />
                      </div>
                    </div>
                    {/* Reset button */}
                    {config.logo && (
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, logo: undefined })}
                        className="text-[#6F675F] hover:text-[#8B5C58] transition-colors tracking-[0.1em] uppercase border border-[#E6E0D7] rounded-lg px-4 py-2 hover:border-[#8B5C58]/30"
                        style={{ fontSize: "10px" }}
                      >
                        Reset to default logo
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Brand Tab: Footer contact (email, phone, address) */}
      {activeTab === "brand" && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] mt-6">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Footer contact
          </h2>
          <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px", fontWeight: 300 }}>
            Shown in the footer below the logo. Leave blank to hide.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ConfigInput
              label="Contact email"
              value={config.contact_email ?? ""}
              onChange={(v) => setConfig({ ...config, contact_email: v })}
            />
            <ConfigInput
              label="Telephone number"
              value={config.contact_phone ?? ""}
              onChange={(v) => setConfig({ ...config, contact_phone: v })}
            />
            <ConfigInput
              label="Business address"
              value={config.contact_address ?? ""}
              onChange={(v) => setConfig({ ...config, contact_address: v })}
            />
          </div>
        </div>
      )}

      {/* Brand Tab: Social Links & Slogan */}
      {activeTab === "brand" && (
        <>
          {/* Slogan */}
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] mt-6">
            <h2 className="text-[#2D2A26] tracking-[0.08em] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
              Footer Slogan
            </h2>
            <ConfigInput
              label="Tagline"
              value={config.slogan || "Crafted with Intention. Shared with Passion."}
              onChange={(v) => setConfig({ ...config, slogan: v })}
            />
            <div className="mt-4">
              <TranslationsPanel
                fields={[
                  { key: "slogan", label: "Tagline", englishValue: config.slogan || "Crafted with Intention. Shared with Passion." },
                ]}
                i18n={config.sloganI18n || {}}
                onChange={(i18n) => setConfig({ ...config, sloganI18n: i18n })}
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] mt-6">
            <h2 className="text-[#2D2A26] tracking-[0.08em] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
              Social Media Links
            </h2>
            <p className="text-[#6F675F] mb-6" style={{ fontSize: "13px", fontWeight: 300 }}>
              Add your social media URLs. Leave empty to hide the icon in the footer.
            </p>
            <div className="space-y-3">
              {([
                { key: "instagram", label: "Instagram", placeholder: "https://www.instagram.com/moecha_japan/" },
                { key: "tiktok", label: "TikTok", placeholder: "https://www.tiktok.com/@moecha_japan" },
                { key: "twitter", label: "X (Twitter)", placeholder: "https://twitter.com/moecha_japan" },
                { key: "facebook", label: "Facebook", placeholder: "https://www.facebook.com/moecha_japan" },
                { key: "youtube", label: "YouTube", placeholder: "https://www.youtube.com/@moecha_japan" },
                { key: "pinterest", label: "Pinterest", placeholder: "https://www.pinterest.com/moecha_japan/" },
                { key: "linkedin", label: "LinkedIn", placeholder: "https://www.linkedin.com/company/moecha/" },
              ] as const).map((social) => {
                const links = config.socialLinks || {};
                return (
                  <div key={social.key} className="flex items-center gap-3">
                    <label className="w-28 shrink-0 text-[#6F675F] tracking-[0.12em] uppercase" style={{ fontSize: "10px" }}>
                      {social.label}
                    </label>
                    <input
                      value={(links as Record<string, string | undefined>)[social.key] || ""}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          socialLinks: { ...links, [social.key]: e.target.value },
                        })
                      }
                      placeholder={social.placeholder}
                      maxLength={200}
                      className="flex-1 px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/50 focus:outline-none focus:border-[#C7A76C]"
                      style={{ fontSize: "13px" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Hero Tab */}
      {activeTab === "hero" && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Hero Section
          </h2>
          <div className="space-y-4">
            <ConfigInput label="Hero Title" value={config.hero.title} onChange={(v) => setConfig({ ...config, hero: { ...config.hero, title: v } })} />
            <ConfigInput label="Subtitle" value={config.hero.subtitle} onChange={(v) => setConfig({ ...config, hero: { ...config.hero, subtitle: v } })} />
            <ConfigInput label="CTA Text" value={config.hero.cta_text} onChange={(v) => setConfig({ ...config, hero: { ...config.hero, cta_text: v } })} />
            <ImageUpload
              label="Hero Image"
              value={config.hero.image}
              onChange={(v) => setConfig({ ...config, hero: { ...config.hero, image: v } })}
              accept={["image/png", "image/jpeg", "image/webp"]}
              acceptLabel="PNG, JPG, WEBP"
              maxSizeBytes={5 * 1024 * 1024}
              maxSizeLabel="5 MB"
              previewAspect="aspect-video"
            />
          </div>
          <div className="mt-6">
            <TranslationsPanel
              fields={[
                { key: "title", label: "Hero Title", englishValue: config.hero.title },
                { key: "subtitle", label: "Subtitle", englishValue: config.hero.subtitle },
                { key: "cta_text", label: "CTA Text", englishValue: config.hero.cta_text },
              ]}
              i18n={config.hero.i18n || {}}
              onChange={(i18n) => setConfig({ ...config, hero: { ...config.hero, i18n } })}
            />
          </div>
        </div>
      )}

      {/* About Tab */}
      {activeTab === "about" && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            About / Editorial Section
          </h2>
          <div className="space-y-4">
            <ConfigInput label="Label" value={config.about.label} onChange={(v) => setConfig({ ...config, about: { ...config.about, label: v } })} />
            <ConfigInput label="Title" value={config.about.title} onChange={(v) => setConfig({ ...config, about: { ...config.about, title: v } })} />
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Paragraph 1</label>
              <textarea rows={4} value={config.about.paragraph1} onChange={(e) => setConfig({ ...config, about: { ...config.about, paragraph1: e.target.value } })} className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] resize-none" style={{ fontSize: "13px" }} />
            </div>
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Paragraph 2</label>
              <textarea rows={4} value={config.about.paragraph2} onChange={(e) => setConfig({ ...config, about: { ...config.about, paragraph2: e.target.value } })} className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] resize-none" style={{ fontSize: "13px" }} />
            </div>
            <ConfigInput label="CTA Button Text" value={config.about.cta_text} onChange={(v) => setConfig({ ...config, about: { ...config.about, cta_text: v } })} />
            <ImageUpload
              label="About Image"
              value={config.about.image}
              onChange={(v) => setConfig({ ...config, about: { ...config.about, image: v } })}
              accept={["image/png", "image/jpeg", "image/webp"]}
              acceptLabel="PNG, JPG, WEBP"
              maxSizeBytes={5 * 1024 * 1024}
              maxSizeLabel="5 MB"
              previewAspect="aspect-[4/5]"
            />
          </div>
          <div className="mt-6">
            <TranslationsPanel
              fields={[
                { key: "label", label: "Label", englishValue: config.about.label },
                { key: "title", label: "Title", englishValue: config.about.title },
                { key: "paragraph1", label: "Paragraph 1", type: "textarea", rows: 3, englishValue: config.about.paragraph1 },
                { key: "paragraph2", label: "Paragraph 2", type: "textarea", rows: 3, englishValue: config.about.paragraph2 },
                { key: "cta_text", label: "CTA Button Text", englishValue: config.about.cta_text },
              ]}
              i18n={config.about.i18n || {}}
              onChange={(i18n) => setConfig({ ...config, about: { ...config.about, i18n } })}
            />
          </div>
        </div>
      )}

      {/* Features Tab */}
      {activeTab === "features" && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Feature Toggles
          </h2>
          <p className="text-[#6F675F] mb-6" style={{ fontSize: "13px", fontWeight: 300 }}>
            Enable or disable storefront features. Changes take effect after saving.
          </p>
          <div className="space-y-4">
            {[
              { key: "reviews" as const, label: "Customer Reviews", desc: "Star ratings and written reviews on product pages" },
              { key: "newsletter" as const, label: "Newsletter Signup", desc: "Email capture section on the homepage", requiresConfig: true },
              { key: "promoCodes" as const, label: "Promo Codes", desc: "Discount code field in the checkout flow" },
              { key: "faq" as const, label: "FAQ / Brewing Guide", desc: "Matcha guide and FAQ section on the homepage" },
              { key: "wishlist" as const, label: "Wishlist", desc: "Heart icon in navigation for saving favourite products" },
              { key: "account" as const, label: "Customer Account", desc: "Account icon in navigation linking to /account (sign in, orders)" },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-4 border-b border-[#E6E0D7] last:border-0">
                <div>
                  <p className="text-[#2D2A26]" style={{ fontSize: "14px" }}>{item.label}</p>
                  <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>{item.desc}</p>
                </div>
                <button
                  onClick={() => toggleFeature(item.key)}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                    features[item.key] ? "bg-[#6F7F63]" : "bg-[#E6E0D7]"
                  }`}
                  role="switch"
                  aria-checked={features[item.key]}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300`}
                    style={{ transform: features[item.key] ? "translateX(26px)" : "translateX(2px)" }}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Newsletter disclaimer */}
          <div className="mt-6 flex items-start gap-3 bg-[#C7A76C]/8 border border-[#C7A76C]/25 rounded-lg px-5 py-4">
            <div className="shrink-0 w-5 h-5 rounded-full bg-[#C7A76C]/15 flex items-center justify-center mt-0.5">
              <span className="text-[#C7A76C]" style={{ fontSize: "12px" }}>!</span>
            </div>
            <div>
              <p className="text-[#2D2A26] mb-1" style={{ fontSize: "13px" }}>
                Newsletter requires additional configuration
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                Enabling the newsletter feature requires an email delivery service (Resend) to be configured by your web designer. 
                Please contact your web designer or developer to set this up before enabling this feature. 
                Toggling it on without the backend configured will display the signup form but emails will not be delivered.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Theme Tab */}
      {activeTab === "theme" && (
        <AdminThemeTab config={config} setConfig={setConfig} />
      )}

      {/* FAQ Tab */}
      {activeTab === "faq" && (
        <AdminFAQTab config={config} setConfig={setConfig} />
      )}

      {/* Wholesale Tab */}
      {activeTab === "wholesale" && (
        <AdminWholesaleTab config={config} setConfig={setConfig} />
      )}

      {/* Promo Codes Tab */}
      {activeTab === "promos" && (
        <PromoCodeManager
          promoCodes={promoCodes}
          addPromoCode={addPromoCode}
          updatePromoCode={updatePromoCode}
          deletePromoCode={deletePromoCode}
          getNextPromoCodeId={getNextPromoCodeId}
        />
      )}

      {/* Reviews Tab */}
      {activeTab === "reviews" && (
        <ReviewManager
          reviews={reviews}
          pendingReviews={pendingReviews}
          approvedReviews={approvedReviews}
          products={products}
          updateReview={updateReview}
          deleteReview={deleteReview}
        />
      )}
    </div>
  );
}

function ConfigInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
        style={{ fontSize: "13px" }}
      />
    </div>
  );
}

function PromoCodeManager({
  promoCodes,
  addPromoCode,
  updatePromoCode,
  deletePromoCode,
  getNextPromoCodeId,
}: {
  promoCodes: PromoCode[];
  addPromoCode: (p: PromoCode) => Promise<{ error?: string }>;
  updatePromoCode: (p: PromoCode) => Promise<{ error?: string }>;
  deletePromoCode: (id: string) => Promise<{ error?: string }>;
  getNextPromoCodeId: () => string;
}) {
  const [showNew, setShowNew] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", discount_type: "percentage" as "percentage" | "fixed", discount_value: 10, expires_at: "" });
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    setError(null);
    const result = await addPromoCode({
      id: getNextPromoCodeId(),
      code: newCode.code.toUpperCase(),
      discount_type: newCode.discount_type,
      discount_value: newCode.discount_value,
      active: true,
      expires_at: newCode.expires_at || undefined,
    });
    if (result.error) {
      setError(result.error);
      return;
    }
    setNewCode({ code: "", discount_type: "percentage", discount_value: 10, expires_at: "" });
    setShowNew(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
      {error && <p className="text-[#8B5C58] mb-3" style={{ fontSize: "12px" }}>{error}</p>}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
          Promo Codes
        </h2>
        <button
          onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 px-4 py-2 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
          style={{ fontSize: "11px" }}
        >
          <Plus size={14} /> Add Code
        </button>
      </div>

      {showNew && (
        <div className="bg-[#F7F5F1] rounded-lg p-4 mb-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Code</label>
              <input value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value })} className="w-full px-3 py-2 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} placeholder="MATCHA10" />
            </div>
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Type</label>
              <select value={newCode.discount_type} onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value as "percentage" | "fixed" })} className="w-full px-3 py-2 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Value</label>
              <input type="number" value={newCode.discount_value} onChange={(e) => setNewCode({ ...newCode, discount_value: Number(e.target.value) })} className="w-full px-3 py-2 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} />
            </div>
            <div>
              <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>Expires</label>
              <input type="date" value={newCode.expires_at} onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })} className="w-full px-3 py-2 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} />
            </div>
          </div>
          <button onClick={handleAdd} disabled={!newCode.code} className="px-4 py-2 bg-[#C7A76C] text-white rounded-lg hover:bg-[#B89650] transition-colors tracking-[0.1em] uppercase disabled:opacity-40" style={{ fontSize: "11px" }}>
            Create Code
          </button>
        </div>
      )}

      {promoCodes.length === 0 ? (
        <p className="text-[#6F675F] text-center py-8" style={{ fontSize: "13px", fontWeight: 300 }}>No promo codes yet.</p>
      ) : (
        <div className="space-y-3">
          {promoCodes.map((promo) => (
            <div key={promo.id} className="flex items-center justify-between py-3 border-b border-[#E6E0D7] last:border-0">
              <div className="flex items-center gap-4">
                <span className="px-3 py-1 bg-[#F2EEE8] rounded-lg text-[#2D2A26] tracking-[0.1em] uppercase" style={{ fontSize: "13px", fontFamily: "'Jost', sans-serif" }}>
                  {promo.code}
                </span>
                <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>
                  {promo.discount_type === "percentage" ? `${promo.discount_value}% off` : `€${promo.discount_value} off`}
                </span>
                {promo.expires_at && (
                  <span className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>
                    Expires {new Date(promo.expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    setError(null);
                    const result = await updatePromoCode({ ...promo, active: !promo.active });
                    if (result.error) setError(result.error);
                  }}
                  className={`px-3 py-1 rounded-full transition-colors ${
                    promo.active ? "bg-[#6F7F63]/15 text-[#6F7F63]" : "bg-[#E6E0D7] text-[#6F675F]"
                  }`}
                  style={{ fontSize: "11px" }}
                >
                  {promo.active ? "Active" : "Inactive"}
                </button>
                <button
                  onClick={async () => {
                    setError(null);
                    const result = await deletePromoCode(promo.id);
                    if (result.error) setError(result.error);
                  }}
                  className="text-[#D8D1C7] hover:text-[#8B5C58] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewManager({
  reviews,
  pendingReviews,
  approvedReviews,
  products,
  updateReview,
  deleteReview,
}: {
  reviews: Review[];
  pendingReviews: Review[];
  approvedReviews: Review[];
  products: { id: number; name: string }[];
  updateReview: (r: Review) => Promise<{ error?: string }>;
  deleteReview: (id: string) => Promise<{ error?: string }>;
}) {
  const [error, setError] = useState<string | null>(null);
  const getProductName = (id: number) => products.find((p) => p.id === id)?.name || "Unknown";

  const renderReview = (review: Review) => (
    <div key={review.id} className="flex items-start justify-between py-4 border-b border-[#E6E0D7] last:border-0">
      <div className="flex-1 min-w-0 mr-4">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>{review.name}</span>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} size={10} className={s <= review.rating ? "text-[#C7A76C] fill-[#C7A76C]" : "text-[#E6E0D7]"} />
            ))}
          </div>
          <span className="text-[#D8D1C7]" style={{ fontSize: "10px" }}>{new Date(review.created_at).toLocaleDateString()}</span>
        </div>
        <p className="text-[#6F675F] mb-1" style={{ fontSize: "12px", fontWeight: 300 }}>{review.comment}</p>
        <p className="text-[#C7A76C]" style={{ fontSize: "10px" }}>Product: {getProductName(review.product_id)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!review.approved && (
          <button
            onClick={async () => {
              setError(null);
              const result = await updateReview({ ...review, approved: true });
              if (result.error) setError(result.error);
            }}
            className="p-1.5 text-[#6F7F63] hover:bg-[#6F7F63]/10 rounded-lg transition-colors"
            title="Approve"
          >
            <CheckCircle size={16} />
          </button>
        )}
        {review.approved && (
          <button
            onClick={async () => {
              setError(null);
              const result = await updateReview({ ...review, approved: false });
              if (result.error) setError(result.error);
            }}
            className="p-1.5 text-[#C7A76C] hover:bg-[#C7A76C]/10 rounded-lg transition-colors"
            title="Unapprove"
          >
            <XCircle size={16} />
          </button>
        )}
        <button
          onClick={async () => {
            setError(null);
            const result = await deleteReview(review.id);
            if (result.error) setError(result.error);
          }}
          className="p-1.5 text-[#D8D1C7] hover:text-[#8B5C58] transition-colors"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {error && <p className="text-[#8B5C58]" style={{ fontSize: "12px" }}>{error}</p>}
      {pendingReviews.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h2 className="text-[#2D2A26] tracking-[0.08em] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Pending Reviews ({pendingReviews.length})
          </h2>
          {pendingReviews.map(renderReview)}
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <h2 className="text-[#2D2A26] tracking-[0.08em] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
          Approved Reviews ({approvedReviews.length})
        </h2>
        {approvedReviews.length === 0 ? (
          <p className="text-[#6F675F] text-center py-8" style={{ fontSize: "13px", fontWeight: 300 }}>No approved reviews yet.</p>
        ) : (
          approvedReviews.map(renderReview)
        )}
      </div>
    </div>
  );
}